import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { authService, User } from '../api/services/auth';
import { useTenant } from './TenantContext';

// Configure axios defaults
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add token to axios headers if it exists
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasCheckedAuth = useRef(false);
  const isChecking = useRef(false);
  const { setCurrentTenant } = useTenant();

  const clearError = () => {
    setError(null);
  };

  const setAuthToken = (token: string | null) => {
    if (token) {
      localStorage.setItem('token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setCurrentTenant(null);
    }
  };

  const fetchTenantData = async (tenantId: number) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/tenants/${tenantId}`);
      if (response.data.success && response.data.data) {
        const tenantData = response.data.data;
        if (tenantData.website_url) {
          try {
            const domain = new URL(tenantData.website_url).hostname;
            tenantData.website_url = domain;
          } catch (err) {
            console.error('[AuthContext] Failed to parse website URL:', err);
          }
        }
        setCurrentTenant(tenantData);
      }
    } catch (err) {
      console.error('[AuthContext] Failed to fetch tenant data:', err);
    }
  };

  const checkAuth = useCallback(async () => {
    if (loading || hasCheckedAuth.current || isChecking.current) {
      return;
    }

    try {
      isChecking.current = true;
      setLoading(true);
      const response = await authService.checkAuth();

      if (response.success && response.data) {
        setUser(response.data);
        setError(null);

        // Extract tenant_id from JWT token and fetch tenant data
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.tenant_id) {
              await fetchTenantData(payload.tenant_id);
            }
          } catch (err) {
            console.error('[AuthContext] Failed to parse JWT token:', err);
          }
        }
      } else {
        setUser(null);
        setAuthToken(null);
      }
    } catch (err: any) {
      if (err.response?.status !== 401) {
        setError(err.response?.data?.message || 'Authentication check failed');
      }
      setUser(null);
      setAuthToken(null);
    } finally {
      setLoading(false);
      hasCheckedAuth.current = true;
      isChecking.current = false;
    }
  }, [loading, setCurrentTenant]);

  const login = async (credentials: { email: string; password: string }) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authService.login(credentials);
      if (response.success && response.data?.user && response.data?.token) {
        setUser(response.data.user);
        setAuthToken(response.data.token);
        setError(null);

        // Extract tenant_id from JWT token and fetch tenant data
        try {
          const payload = JSON.parse(atob(response.data.token.split('.')[1]));
          if (payload.tenant_id) {
            await fetchTenantData(payload.tenant_id);
          }
        } catch (err) {
          console.error('Failed to parse JWT token:', err);
        }
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
      setUser(null);
      setAuthToken(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
      setAuthToken(null);
      setError(null);
      hasCheckedAuth.current = false;
    } catch (err: any) {
      if (err.response?.status === 401) {
        setUser(null);
        setAuthToken(null);
        setError(null);
        hasCheckedAuth.current = false;
      } else {
        setError(err.response?.data?.message || 'Logout failed');
      }
    } finally {
      setLoading(false);
    }
  };

  // Check auth status on mount only if we have a token
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !hasCheckedAuth.current && !isChecking.current) {
      checkAuth();
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, checkAuth, clearError }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};